/**
 * Admin Users Management Page
 * 
 * Runtime admin management without redeployment
 * - Add/remove admins via UI
 * - Real-time updates from D1 database
 * - Audit logging for all changes
 */

import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Loader2, UserPlus, Trash2, Shield, Clock, User } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Separator } from "@/components/ui/separator";

interface AdminUser {
  email: string;
  name?: string;
  addedBy?: string;
  createdAt: string;
  notes?: string;
}

export default function AdminUsers() {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  // Fetch admin users
  const { data, isLoading, error, refetch } = useQuery<{ ok: boolean; admins: AdminUser[]; count: number }>({
    queryKey: ["/api/admin/users"],
  });

  // Add admin mutation
  const addMutation = useMutation({
    mutationFn: async (payload: { email: string; name?: string; note?: string }) => {
      const res = await fetch("/api/admin/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const error = await res.json() as { message?: string };
        throw new Error(error.message || "Failed to add admin");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      setEmail("");
      setName("");
      setNote("");
      toast({
        title: "관리자 추가 완료",
        description: "새 관리자가 성공적으로 추가되었습니다.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "추가 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Remove admin mutation
  const removeMutation = useMutation({
    mutationFn: async (email: string) => {
      const res = await fetch(`/api/admin/users/${encodeURIComponent(email)}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const error = await res.json() as { message?: string };
        throw new Error(error.message || "Failed to remove admin");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      setDeleteTarget(null);
      toast({
        title: "관리자 삭제 완료",
        description: "관리자가 성공적으로 삭제되었습니다.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "삭제 실패",
        description: error.message,
        variant: "destructive",
      });
      setDeleteTarget(null);
    },
  });

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast({
        title: "입력 오류",
        description: "이메일을 입력해주세요.",
        variant: "destructive",
      });
      return;
    }
    addMutation.mutate({ email: email.trim(), name: name.trim() || undefined, note: note.trim() || undefined });
  };

  const handleRemove = (email: string) => {
    setDeleteTarget(email);
  };

  const confirmRemove = () => {
    if (deleteTarget) {
      removeMutation.mutate(deleteTarget);
    }
  };

  const admins = data?.admins || [];

  return (
    <div className="container mx-auto py-8 px-4 max-w-5xl">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">관리자 관리</h1>
        </div>
        <p className="text-muted-foreground">
          시스템 관리자를 실시간으로 추가하거나 삭제할 수 있습니다. 재배포 없이 즉시 반영됩니다.
        </p>
      </div>

      {/* Add Admin Form */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            새 관리자 추가
          </CardTitle>
          <CardDescription>
            관리자 권한을 부여할 사용자의 이메일을 입력하세요.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAdd} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="email">이메일 *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={addMutation.isPending}
                  data-testid="input-admin-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">이름 (선택)</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="홍길동"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={addMutation.isPending}
                  data-testid="input-admin-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="note">메모 (선택)</Label>
              <Input
                id="note"
                type="text"
                placeholder="담당 업무, 권한 설명 등"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={addMutation.isPending}
                data-testid="input-admin-note"
              />
            </div>
            <Button 
              type="submit" 
              disabled={addMutation.isPending || !email.trim()}
              data-testid="button-add-admin"
            >
              {addMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  추가 중...
                </>
              ) : (
                <>
                  <UserPlus className="mr-2 h-4 w-4" />
                  관리자 추가
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Admin List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>관리자 목록</CardTitle>
              <CardDescription>
                총 {admins.length}명의 관리자가 등록되어 있습니다.
              </CardDescription>
            </div>
            {!isLoading && !error && (
              <Badge variant="secondary" className="text-sm">
                {admins.length}명
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-destructive">관리자 목록을 불러오는데 실패했습니다.</p>
              <Button variant="outline" className="mt-4" onClick={() => refetch()}>
                다시 시도
              </Button>
            </div>
          ) : admins.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              등록된 관리자가 없습니다.
            </div>
          ) : (
            <div className="space-y-3">
              {admins.map((admin, index) => (
                <div key={admin.email}>
                  {index > 0 && <Separator className="my-3" />}
                  <div 
                    className="flex items-start justify-between gap-4 p-4 rounded-lg hover-elevate"
                    data-testid={`admin-item-${admin.email}`}
                  >
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium" data-testid={`text-admin-email-${admin.email}`}>
                          {admin.email}
                        </span>
                        {admin.name && (
                          <Badge variant="outline" className="text-xs">
                            {admin.name}
                          </Badge>
                        )}
                      </div>
                      
                      {admin.notes && (
                        <p className="text-sm text-muted-foreground pl-6">
                          {admin.notes}
                        </p>
                      )}
                      
                      <div className="flex items-center gap-4 text-xs text-muted-foreground pl-6">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          추가일: {new Date(admin.createdAt).toLocaleDateString('ko-KR')}
                        </div>
                        {admin.addedBy && (
                          <div className="flex items-center gap-1">
                            <Shield className="h-3 w-3" />
                            추가자: {admin.addedBy}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemove(admin.email)}
                      disabled={removeMutation.isPending}
                      data-testid={`button-remove-admin-${admin.email}`}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      삭제
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>관리자 삭제 확인</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteTarget}</strong> 사용자의 관리자 권한을 삭제하시겠습니까?
              <br />
              <br />
              이 작업은 즉시 반영되며, 해당 사용자는 더 이상 관리자 페이지에 접근할 수 없습니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-remove">취소</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmRemove}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-remove"
            >
              삭제
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
